import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { normalizeCompanyBrandingPayload, resolveCompanyBrandingName } from './wpaCompanyBranding';
import WpaPwaManifestAndInstall from './WpaPwaManifestAndInstall';
import { writeSessionScope, clearSessionScope } from './wpaCartStore';
import { resetMobileZoom } from './wpaViewportZoom';

const PRIMARY = '#4CAF50';
const PRIMARY_DARK = '#388E3C';
const BG = '#f5f7f5';
const CARD_SHADOW = '0 4px 24px rgba(0,0,0,0.10)';

const WPA_REMEMBER_KEY = 'wpa_remember_login';
const WPA_SAVED_LOGIN_KEY = 'wpa_saved_login';

const LANGUAGE_OPTIONS = [
  { value: 'zh-TC', label: '繁體中文' },
  { value: 'zh-SC', label: '简体中文' },
  { value: 'en', label: 'English' },
];

const styles = {
  root: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: BG,
    padding: 'max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) max(16px, env(safe-area-inset-bottom)) max(16px, env(safe-area-inset-left))',
    boxSizing: 'border-box',
    overflowY: 'auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  card: {
    width: '100%',
    maxWidth: 400,
    maxHeight: 'min(92vh, 720px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    background: '#fff',
    borderRadius: 16,
    boxShadow: CARD_SHADOW,
    padding: '36px 28px 28px',
    boxSizing: 'border-box',
    flexShrink: 0,
  },
  logoWrap: {
    textAlign: 'center',
    marginBottom: 24,
  },
  logoIcon: {
    width: 88,
    height: 88,
    borderRadius: 20,
    background: PRIMARY,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 44,
    fontWeight: 700,
    marginBottom: 12,
  },
  logoImage: {
    width: 88,
    height: 88,
    borderRadius: 20,
    objectFit: 'contain',
    marginBottom: 12,
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  title: {
    margin: 0,
    fontSize: 'clamp(11px, 3.4vw, 15px)',
    fontWeight: 700,
    color: '#222',
    lineHeight: 1.25,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '100%',
    padding: '0 4px',
    boxSizing: 'border-box',
  },
  subtitle: {
    margin: '4px 0 0',
    fontSize: 14,
    color: '#888',
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 15,
    border: '1px solid #ddd',
    borderRadius: 10,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  inputFocus: {
    borderColor: PRIMARY,
  },
  btn: {
    width: '100%',
    padding: '13px 0',
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    background: PRIMARY,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'background 0.2s',
    marginTop: 8,
  },
  btnDisabled: {
    background: '#a5d6a7',
    cursor: 'not-allowed',
  },
  error: {
    background: '#fff5f5',
    color: '#c0392b',
    borderRadius: 8,
    padding: '10px 14px',
    fontSize: 13,
    marginBottom: 16,
    border: '1px solid #f5c6cb',
  },
  spinner: {
    display: 'inline-block',
    width: 18,
    height: 18,
    border: '3px solid rgba(255,255,255,0.3)',
    borderTop: '3px solid #fff',
    borderRadius: '50%',
    animation: 'wpa-spin 0.7s linear infinite',
    verticalAlign: 'middle',
    marginRight: 8,
  },
  companyCard: {
    padding: '16px 18px',
    background: '#fff',
    border: '1.5px solid #e0e0e0',
    borderRadius: 12,
    marginBottom: 10,
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  companyName: {
    fontSize: 16,
    fontWeight: 600,
    color: '#333',
    margin: 0,
  },
  companyDesc: {
    fontSize: 13,
    color: '#888',
    margin: '2px 0 0',
  },
  fieldGroup: {
    marginBottom: 16,
  },
  passwordFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 4,
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  rememberLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    color: '#444',
    cursor: 'pointer',
    userSelect: 'none',
    flex: '1 1 auto',
    minWidth: 0,
  },
  rememberCheckbox: {
    width: 18,
    height: 18,
    accentColor: PRIMARY,
    cursor: 'pointer',
    flexShrink: 0,
  },
  linkButton: {
    background: 'none',
    border: 'none',
    padding: '4px 0',
    color: PRIMARY,
    fontSize: 13,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    flexShrink: 0,
  },
  langRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: 14,
  },
  langSelect: {
    padding: '8px 12px',
    fontSize: 14,
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    minWidth: 148,
    cursor: 'pointer',
    outline: 'none',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 4000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
    boxSizing: 'border-box',
  },
  modalBox: {
    width: '100%',
    maxWidth: 360,
    background: '#fff',
    borderRadius: 14,
    padding: '22px 20px 18px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
    boxSizing: 'border-box',
  },
  modalTitle: {
    margin: '0 0 12px',
    fontSize: 17,
    fontWeight: 700,
    color: '#222',
  },
  modalBody: {
    fontSize: 14,
    color: '#555',
    lineHeight: 1.55,
    marginBottom: 16,
  },
  modalHint: {
    fontSize: 13,
    color: '#888',
    marginBottom: 10,
  },
  modalBtn: {
    width: '100%',
    padding: '11px 0',
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
    background: PRIMARY,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
  },
  modalOutlineBtn: {
    width: '100%',
    padding: '11px 0',
    fontSize: 15,
    fontWeight: 600,
    color: PRIMARY,
    background: '#fff',
    border: `2px solid ${PRIMARY}`,
    borderRadius: 10,
    cursor: 'pointer',
  },
  modalActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  modalSuccess: {
    fontSize: 14,
    color: '#2e7d32',
    marginBottom: 14,
    lineHeight: 1.55,
  },
  modalErr: {
    fontSize: 13,
    color: '#c0392b',
    marginBottom: 10,
  },
  link: {
    color: PRIMARY,
    cursor: 'pointer',
    fontSize: 13,
    textAlign: 'center',
    marginTop: 14,
  },
};

const loginStylesCss = `@keyframes wpa-spin { to { transform: rotate(360deg); } }
#wpa-login-root { min-height: 100vh; min-height: 100dvh; }`;

const Spinner = () => (
  <span style={styles.spinner} />
);

const SpinnerGray = () => (
  <span
    style={{
      display: 'inline-block',
      width: 22,
      height: 22,
      border: '3px solid rgba(0,0,0,0.12)',
      borderTop: `3px solid ${PRIMARY}`,
      borderRadius: '50%',
      animation: 'wpa-spin 0.7s linear infinite',
      verticalAlign: 'middle',
    }}
  />
);

const WpaLoginPage = () => {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyId } = useParams();

  const token = searchParams.get('token');

  const [mode, setMode] = useState(token ? 'otp' : 'email');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // email/password login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberLogin, setRememberLogin] = useState(() => localStorage.getItem(WPA_REMEMBER_KEY) === '1');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotPhase, setForgotPhase] = useState('menu');
  const [forgotNonce, setForgotNonce] = useState('');
  const [forgotMaskedPhone, setForgotMaskedPhone] = useState('');
  const [forgotMaskedEmail, setForgotMaskedEmail] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');
  const [forgotNewPwd, setForgotNewPwd] = useState('');
  const [forgotConfirmPwd, setForgotConfirmPwd] = useState('');
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotErr, setForgotErr] = useState('');

  // OTP：僅 URL 帶 token 的 WhatsApp 簽名連結登入（非主登入表單）
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpNonce, setOtpNonce] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');

  // 忘記密碼：依 PWA 帳號資料決定可顯示 WhatsApp / 電郵
  const [forgotMethods, setForgotMethods] = useState(null);
  const [forgotMethodsLoading, setForgotMethodsLoading] = useState(false);

  // company selection
  const [needSelectCompany, setNeedSelectCompany] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [tempToken, setTempToken] = useState('');
  const [selectingCompany, setSelectingCompany] = useState(false);
  const [sessionChecking, setSessionChecking] = useState(false);

  // company branding（logo 走 /api/wpa-auth/company-logo，不需登入即可載入）
  const [companyBranding, setCompanyBranding] = useState(() => ({ name: '', nameLocales: null }));
  const [logoLoadFailed, setLogoLoadFailed] = useState(false);
  const documentTitleBeforeLoginRef = useRef(null);

  // focus tracking for input styles
  const [focusedField, setFocusedField] = useState('');

  const getInputStyle = (field) => ({
    ...styles.input,
    ...(focusedField === field ? styles.inputFocus : {}),
  });

  const handleApiError = useCallback((data, fallbackMsg) => {
    setError(data?.message || fallbackMsg);
  }, []);

  useEffect(() => {
    if (localStorage.getItem(WPA_REMEMBER_KEY) === '1') {
      const saved = localStorage.getItem(WPA_SAVED_LOGIN_KEY);
      if (saved && companyId) {
        setEmail(saved);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadForgotMethods = useCallback(async (loginId) => {
    if (!loginId?.trim() || !companyId) {
      setForgotMethods(null);
      return;
    }
    setForgotMethodsLoading(true);
    setForgotMethods(null);
    try {
      const res = await fetch('/api/wpa-auth/login-methods', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginId: loginId.trim(), companyId }),
      });
      if (res.ok) setForgotMethods(await res.json());
      else setForgotMethods({ hasPhone: false, hasEmail: false });
    } catch {
      setForgotMethods({ hasPhone: false, hasEmail: false });
    } finally {
      setForgotMethodsLoading(false);
    }
  }, [companyId]);

  /** 仍有有效 wpa_token 且公司一致時，直接進入 WPA 首頁（自動登入體感） */
  useEffect(() => {
    if (!companyId || token) {
      setSessionChecking(false);
      return undefined;
    }
    const stored = localStorage.getItem('wpa_token');
    if (!stored) {
      setSessionChecking(false);
      return undefined;
    }
    setSessionChecking(true);
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/wpa-auth/me', {
          headers: { Authorization: `Bearer ${stored}` },
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.success && data.companyId != null) {
          const sid = String(data.companyId).toLowerCase().replace(/[{}]/g, '');
          const cid = String(companyId).toLowerCase().replace(/[{}]/g, '');
          if (sid === cid) {
            if (data.identity?.id) {
              writeSessionScope(data.companyId, data.identity.id);
            }
            navigate(`/${companyId}/wpa/home`, { replace: true });
            return;
          }
        }
        localStorage.removeItem('wpa_token');
        localStorage.removeItem('wpa_userInfo');
        clearSessionScope();
      } catch {
        if (!cancelled) {
          localStorage.removeItem('wpa_token');
          localStorage.removeItem('wpa_userInfo');
          clearSessionScope();
        }
      } finally {
        if (!cancelled) setSessionChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId, token, navigate]);

  useEffect(() => {
    setLogoLoadFailed(false);
  }, [companyId]);

  useEffect(() => {
    if (!showForgotModal) return;
    setForgotPhase('menu');
    setForgotNonce('');
    setForgotMaskedPhone('');
    setForgotMaskedEmail('');
    setForgotOtp('');
    setForgotNewPwd('');
    setForgotConfirmPwd('');
    setForgotBusy(false);
    setForgotMsg('');
    setForgotErr('');
    if (email.trim()) {
      loadForgotMethods(email);
    } else {
      setForgotMethods(null);
      setForgotMethodsLoading(false);
    }
  }, [showForgotModal, email, loadForgotMethods]);

  const companyDisplayName = useMemo(
    () => resolveCompanyBrandingName(companyBranding, currentLanguage),
    [companyBranding, currentLanguage],
  );

  // Fetch company branding（logo 由匿名 API 圖片網址載入）
  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const res = await fetch(`/api/wpa-auth/company-branding/${companyId}`);
        if (res.ok) {
          const data = await res.json();
          setCompanyBranding(normalizeCompanyBrandingPayload(data));
        }
      } catch {
        // ignore - will fall back to defaults
      }
    })();
  }, [companyId]);

  // 瀏覽器分頁標題：顯示公司名（載入前短暫顯示「登入」）；僅在離開本頁時還原為進入前的 title
  useEffect(() => {
    if (documentTitleBeforeLoginRef.current === null) {
      documentTitleBeforeLoginRef.current = document.title;
    }
    if (companyDisplayName) {
      document.title = companyDisplayName;
    } else if (companyId) {
      document.title = t('wpaLogin.login');
    }
  }, [companyDisplayName, companyId, t]);

  useEffect(() => {
    return () => {
      const prev = documentTitleBeforeLoginRef.current;
      if (prev != null) document.title = prev;
    };
  }, []);

  // WhatsApp token validation -> start OTP flow
  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    const startWhatsApp = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch('/api/wpa-auth/whatsapp/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, companyId }),
        });
        const data = await res.json();
        if (!res.ok || !data.success) {
          if (!cancelled) handleApiError(data, t('wpaLogin.linkValidateFailed'));
          return;
        }
        if (!cancelled) {
          setWhatsappPhone(data.phoneNo || data.phone || '');
          setOtpNonce(data.nonce || '');
          setOtpSent(true);
        }
      } catch {
        if (!cancelled) setError(t('wpaLogin.networkError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    startWhatsApp();
    return () => { cancelled = true; };
  }, [token, companyId, handleApiError, t]);

  const persistRememberCredentials = useCallback((loginName) => {
    const name = (loginName || '').trim();
    if (rememberLogin && name) {
      localStorage.setItem(WPA_REMEMBER_KEY, '1');
      localStorage.setItem(WPA_SAVED_LOGIN_KEY, name);
    } else {
      localStorage.removeItem(WPA_REMEMBER_KEY);
      localStorage.removeItem(WPA_SAVED_LOGIN_KEY);
    }
  }, [rememberLogin]);

  const handleLoginSuccess = useCallback((data, loginNameForRemember) => {
    if (data.needSelectCompany) {
      setTempToken(data.tempToken || data.token);
      setCompanies(data.availableCompanies || data.companies || []);
      setNeedSelectCompany(true);
    } else {
      if (loginNameForRemember != null) persistRememberCredentials(loginNameForRemember);
      localStorage.setItem('wpa_token', data.token);
      localStorage.setItem('wpa_userInfo', JSON.stringify(data.identity || data.userInfo || {}));
      if (data.identity?.id) {
        writeSessionScope(companyId, data.identity.id);
      }
      // 登入後跳轉前，先把 iOS 為了 email/password input 留下的 auto-zoom 還原
      resetMobileZoom();
      navigate(`/${companyId}/wpa/home`);
    }
  }, [companyId, navigate, persistRememberCredentials]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/wpa-auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginId: email.trim(), email: email.trim(), password, companyId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        handleApiError(data, t('wpaLogin.loginFailed'));
        return;
      }
      handleLoginSuccess(data, email.trim());
    } catch {
      setError(t('wpaLogin.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim() || !otpNonce) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/wpa-auth/whatsapp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, nonce: otpNonce, otp: otp.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        handleApiError(data, t('wpaLogin.otpFailed'));
        return;
      }
      handleLoginSuccess(data, null);
    } catch {
      setError(t('wpaLogin.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (selectedCompanyId) => {
    setSelectingCompany(true);
    setError('');
    try {
      const res = await fetch('/api/wpa-auth/select-company', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Wpa-Temp-Token': tempToken,
        },
        body: JSON.stringify({ companyId: selectedCompanyId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        handleApiError(data, t('wpaLogin.companySelectFailed'));
        return;
      }
      persistRememberCredentials(email.trim());
      localStorage.setItem('wpa_token', data.token);
      localStorage.setItem('wpa_userInfo', JSON.stringify(data.identity || data.userInfo || {}));
      if (data.identity?.id) {
        writeSessionScope(selectedCompanyId, data.identity.id);
      }
      // 多公司帳號選完公司後，跳 home 前先還原 iOS 縮放
      resetMobileZoom();
      navigate(`/${selectedCompanyId}/wpa/home`);
    } catch {
      setError(t('wpaLogin.networkError'));
    } finally {
      setSelectingCompany(false);
    }
  };

  // --- Render helpers ---

  const renderHeader = () => {
    const subtitleText = needSelectCompany
      ? t('wpaLogin.selectCompany')
      : mode === 'otp'
        ? t('wpaLogin.whatsappOtpLogin')
        : null;

    return (
      <div style={styles.logoWrap}>
        {companyId && !logoLoadFailed ? (
          <img
            src={`/api/wpa-auth/company-logo/${companyId}`}
            alt=""
            style={styles.logoImage}
            onError={() => setLogoLoadFailed(true)}
          />
        ) : (
          <div style={styles.logoIcon}>W</div>
        )}
        {companyDisplayName && <h1 style={styles.title}>{companyDisplayName}</h1>}
        {subtitleText && (
          <p style={{ ...styles.subtitle, ...(!companyDisplayName ? { fontSize: 18, fontWeight: 600, color: '#222' } : {}) }}>
            {subtitleText}
          </p>
        )}
      </div>
    );
  };

  const renderError = () =>
    error ? <div style={styles.error}>{error}</div> : null;

  const openForgotPassword = () => {
    if (!email.trim()) {
      setError(t('wpaLogin.enterLoginNameFirst'));
      return;
    }
    setShowForgotModal(true);
    setError('');
  };

  const startForgotEmail = async () => {
    if (!companyId) return;
    if (!email.trim()) {
      setForgotErr(t('wpaLogin.enterLoginNameFirst'));
      return;
    }
    setForgotBusy(true);
    setForgotErr('');
    try {
      const res = await fetch('/api/wpa-auth/forgot-password/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, loginId: email.trim(), loginEmail: email.trim() }),
      });
      const data = await res.json();
      if (data.challengeStarted && data.nonce) {
        setForgotPhase('email');
        setForgotNonce(data.nonce);
        setForgotMaskedEmail(data.email || '');
      } else {
        setForgotErr(t('wpaLogin.forgotPasswordEmailUnavailable'));
      }
    } catch {
      setForgotErr(t('wpaLogin.networkError'));
    } finally {
      setForgotBusy(false);
    }
  };

  const startForgotPhone = async () => {
    if (!companyId) return;
    if (!email.trim()) {
      setForgotErr(t('wpaLogin.enterLoginNameFirst'));
      return;
    }
    setForgotBusy(true);
    setForgotErr('');
    try {
      const res = await fetch('/api/wpa-auth/forgot-password/phone/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ companyId, loginId: email.trim(), loginEmail: email.trim() }),
      });
      const data = await res.json();
      if (data.challengeStarted && data.nonce) {
        setForgotPhase('phone');
        setForgotNonce(data.nonce);
        setForgotMaskedPhone(data.phoneNo || '');
      } else {
        setForgotErr(t('wpaLogin.forgotPasswordPhoneUnavailable'));
      }
    } catch {
      setForgotErr(t('wpaLogin.networkError'));
    } finally {
      setForgotBusy(false);
    }
  };

  const submitForgotPhone = async (e) => {
    e.preventDefault();
    if (!companyId || !forgotNonce) return;
    if (forgotNewPwd.length < 8) {
      setForgotErr(t('wpaLogin.passwordMinLength'));
      return;
    }
    if (forgotNewPwd !== forgotConfirmPwd) {
      setForgotErr(t('wpaLogin.forgotPasswordMismatch'));
      return;
    }
    setForgotBusy(true);
    setForgotErr('');
    try {
      const res = await fetch('/api/wpa-auth/forgot-password/phone/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId,
          nonce: forgotNonce,
          otp: forgotOtp.trim(),
          newPassword: forgotNewPwd,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        setForgotErr(data.message || t('wpaLogin.otpFailed'));
        return;
      }
      setForgotErr('');
      setForgotPhase('menu');
      setForgotMsg(t('wpaLogin.forgotPasswordPhoneSuccess'));
    } catch {
      setForgotErr(t('wpaLogin.networkError'));
    } finally {
      setForgotBusy(false);
    }
  };

  const renderEmailForm = () => (
    <form onSubmit={handleEmailLogin}>
      <div style={styles.fieldGroup}>
        <label style={styles.label}>{t('wpaLogin.loginName')}</label>
        <input
          type="text"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
          onFocus={() => setFocusedField('email')}
          onBlur={() => {
            setFocusedField('');
            if (rememberLogin && email.trim()) {
              localStorage.setItem(WPA_REMEMBER_KEY, '1');
              localStorage.setItem(WPA_SAVED_LOGIN_KEY, email.trim());
            }
          }}
          style={getInputStyle('email')}
          placeholder=""
          autoComplete="username"
          required
        />
      </div>
      <div style={{ ...styles.fieldGroup, marginBottom: 8 }}>
        <label style={styles.label}>{t('wpaLogin.password')}</label>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onFocus={() => setFocusedField('password')}
          onBlur={() => setFocusedField('')}
          style={getInputStyle('password')}
          placeholder={t('wpaLogin.passwordPlaceholder')}
          autoComplete="current-password"
          required
        />
      </div>
      <div style={styles.passwordFooter}>
        <label style={styles.rememberLabel}>
          <input
            type="checkbox"
            checked={rememberLogin}
            onChange={(e) => {
              const v = e.target.checked;
              setRememberLogin(v);
              if (!v) {
                localStorage.removeItem(WPA_REMEMBER_KEY);
                localStorage.removeItem(WPA_SAVED_LOGIN_KEY);
              } else {
                localStorage.setItem(WPA_REMEMBER_KEY, '1');
                if (email.trim()) {
                  localStorage.setItem(WPA_SAVED_LOGIN_KEY, email.trim());
                }
              }
            }}
            style={styles.rememberCheckbox}
          />
          <span>{t('wpaLogin.rememberLogin')}</span>
        </label>
        <button type="button" style={styles.linkButton} onClick={openForgotPassword}>
          {t('wpaLogin.forgotPassword')}
        </button>
      </div>
      {rememberLogin ? (
        <p style={{ fontSize: 12, color: '#888', margin: '0 0 12px', lineHeight: 1.45 }}>
          {t('wpaLogin.rememberLoginHint')}
        </p>
      ) : null}
      <div style={styles.langRow}>
        <select
          value={currentLanguage}
          onChange={(e) => changeLanguage(e.target.value)}
          style={styles.langSelect}
          aria-label={t('wpaLogin.languageAria')}
        >
          {LANGUAGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>
      <button
        type="submit"
        disabled={loading || sessionChecking}
        style={{ ...styles.btn, ...((loading || sessionChecking) ? styles.btnDisabled : {}), marginTop: 0 }}
        onMouseEnter={(e) => { if (!loading && !sessionChecking) e.target.style.background = PRIMARY_DARK; }}
        onMouseLeave={(e) => { if (!loading && !sessionChecking) e.target.style.background = PRIMARY; }}
      >
        {loading ? <><Spinner />{t('wpaLogin.loggingIn')}</> : t('wpaLogin.login')}
      </button>

    </form>
  );

  const renderOtpForm = () => {
    if (!otpSent) {
      return (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spinner />
          <p style={{ color: '#666', marginTop: 12 }}>{t('wpaLogin.validatingWhatsappLink')}</p>
        </div>
      );
    }

    return (
      <form onSubmit={handleVerifyOtp}>
        {whatsappPhone && (
          <p style={{ fontSize: 14, color: '#555', marginBottom: 16, textAlign: 'center' }}>
            {t('wpaLogin.otpSentTo')} <strong>{whatsappPhone}</strong>
          </p>
        )}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>{t('wpaLogin.otpCode')}</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
            onFocus={() => setFocusedField('otp')}
            onBlur={() => setFocusedField('')}
            style={{ ...getInputStyle('otp'), textAlign: 'center', letterSpacing: 8, fontSize: 22 }}
            placeholder="------"
            autoComplete="one-time-code"
            required
          />
        </div>
        <button
          type="submit"
          disabled={loading || otp.length < 4 || !otpNonce}
          style={{ ...styles.btn, ...(loading || otp.length < 4 || !otpNonce ? styles.btnDisabled : {}) }}
          onMouseEnter={(e) => { if (!loading) e.target.style.background = PRIMARY_DARK; }}
          onMouseLeave={(e) => { if (!loading) e.target.style.background = PRIMARY; }}
        >
          {loading ? <><Spinner />{t('wpaLogin.verifying')}</> : t('wpaLogin.verify')}
        </button>
        <div
          style={styles.link}
          onClick={() => { setMode('email'); setError(''); }}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('email'); setError(''); } }}
        >
          {t('wpaLogin.useAccountPassword')}
        </div>
      </form>
    );
  };

  const renderCompanySelection = () => (
    <div>
      {selectingCompany && (
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Spinner />
        </div>
      )}
      <div style={{ maxHeight: 340, overflowY: 'auto' }}>
        {companies.map((company) => (
          <div
            key={company.id}
            style={styles.companyCard}
            onClick={() => !selectingCompany && handleSelectCompany(company.id)}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = PRIMARY;
              e.currentTarget.style.boxShadow = `0 2px 8px ${PRIMARY}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e0e0e0';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <p style={styles.companyName}>{company.name}</p>
            {company.description && (
              <p style={styles.companyDesc}>{company.description}</p>
            )}
          </div>
        ))}
      </div>
      <div
        style={{ ...styles.link, marginTop: 18 }}
        onClick={() => { setNeedSelectCompany(false); setError(''); }}
      >
        {t('wpaLogin.backToLogin')}
      </div>
    </div>
  );

  const renderBody = () => {
    if (needSelectCompany) return renderCompanySelection();
    if (mode === 'otp') return renderOtpForm();
    if (sessionChecking) {
      return (
        <div style={{ textAlign: 'center', padding: '36px 0' }}>
          <SpinnerGray />
          <p style={{ color: '#666', marginTop: 16, fontSize: 14 }}>{t('wpaLogin.sessionRestoring')}</p>
        </div>
      );
    }
    return renderEmailForm();
  };

  return (
    <>
      <WpaPwaManifestAndInstall companyId={companyId} />
      <style>{loginStylesCss}</style>
      <div id="wpa-login-root" style={styles.root}>
        <div style={styles.card}>
          {renderHeader()}
          {renderError()}
          {renderBody()}
        </div>
      </div>
      {showForgotModal && (
        <div
          style={styles.modalOverlay}
          role="presentation"
          onClick={() => setShowForgotModal(false)}
        >
          <div
            style={styles.modalBox}
            role="dialog"
            aria-labelledby="wpa-forgot-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="wpa-forgot-title" style={styles.modalTitle}>{t('wpaLogin.forgotPasswordTitle')}</h2>
            {forgotErr ? <div style={styles.modalErr}>{forgotErr}</div> : null}
            {forgotMsg ? (
              <>
                <p style={styles.modalSuccess}>{forgotMsg}</p>
                <button
                  type="button"
                  style={styles.modalBtn}
                  onClick={() => setShowForgotModal(false)}
                >
                  {t('wpa.layout.installGotIt')}
                </button>
              </>
            ) : forgotPhase === 'phone' || forgotPhase === 'email' ? (
              <form onSubmit={submitForgotPhone}>
                {forgotPhase === 'phone' && forgotMaskedPhone ? (
                  <p style={styles.modalBody}>
                    {t('wpaLogin.otpSentTo')} <strong>{forgotMaskedPhone}</strong>
                  </p>
                ) : null}
                {forgotPhase === 'email' && forgotMaskedEmail ? (
                  <p style={styles.modalBody}>
                    {t('wpaLogin.emailOtpSentTo')} <strong>{forgotMaskedEmail}</strong>
                  </p>
                ) : null}
                <label style={styles.label}>{t('wpaLogin.otpCode')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={forgotOtp}
                  onChange={(ev) => setForgotOtp(ev.target.value.replace(/\D/g, ''))}
                  style={{ ...styles.input, marginBottom: 12, textAlign: 'center', letterSpacing: 6, fontSize: 20 }}
                  placeholder="------"
                  autoComplete="one-time-code"
                  required
                />
                <label style={styles.label}>{t('wpaLogin.forgotPasswordNewPassword')}</label>
                <input
                  type="password"
                  value={forgotNewPwd}
                  onChange={(ev) => setForgotNewPwd(ev.target.value)}
                  style={{ ...styles.input, marginBottom: 12 }}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <label style={styles.label}>{t('wpaLogin.forgotPasswordConfirmPassword')}</label>
                <input
                  type="password"
                  value={forgotConfirmPwd}
                  onChange={(ev) => setForgotConfirmPwd(ev.target.value)}
                  style={{ ...styles.input, marginBottom: 12 }}
                  autoComplete="new-password"
                  minLength={8}
                  required
                />
                <button
                  type="submit"
                  disabled={forgotBusy || forgotOtp.length < 4}
                  style={{ ...styles.modalBtn, ...(forgotBusy || forgotOtp.length < 4 ? styles.btnDisabled : {}) }}
                >
                  {forgotBusy ? <><Spinner />{t('wpaLogin.verifying')}</> : t('wpaLogin.forgotPasswordSubmitPhone')}
                </button>
                <button
                  type="button"
                  style={{ ...styles.link, marginTop: 10 }}
                  onClick={() => { setForgotPhase('menu'); setForgotErr(''); }}
                >
                  {t('wpaLogin.backToLogin')}
                </button>
              </form>
            ) : (
              <>
                {!email.trim() ? (
                  <p style={styles.modalHint}>{t('wpaLogin.enterLoginNameFirst')}</p>
                ) : null}
                <p style={styles.modalBody}>{t('wpaLogin.forgotPasswordGuide')}</p>
                {forgotMethodsLoading ? (
                  <div style={{ textAlign: 'center', padding: '12px 0' }}>
                    <SpinnerGray />
                  </div>
                ) : email.trim() && forgotMethods && !forgotMethods.hasPhone && !forgotMethods.hasEmail ? (
                  <p style={styles.modalHint}>{t('wpaLogin.forgotPasswordNoChannels')}</p>
                ) : null}
                <div style={styles.modalActions}>
                  {forgotMethods?.hasPhone && (
                    <button
                      type="button"
                      style={{
                        ...styles.modalOutlineBtn,
                        opacity: forgotBusy ? 0.6 : 1,
                        color: '#075e54',
                        borderColor: '#25d366',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                      }}
                      disabled={forgotBusy || !email.trim()}
                      onClick={startForgotPhone}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#25d366" aria-hidden="true">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      {t('wpaLogin.sendWhatsappOtp')}
                      {forgotMethods.maskedPhone ? (
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#666' }}>({forgotMethods.maskedPhone})</span>
                      ) : null}
                    </button>
                  )}
                  {forgotMethods?.hasEmail && (
                    <button
                      type="button"
                      style={{
                        ...styles.modalOutlineBtn,
                        opacity: forgotBusy ? 0.6 : 1,
                        color: '#1565c0',
                        borderColor: '#4285f4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginTop: forgotMethods?.hasPhone ? 8 : 0,
                      }}
                      disabled={forgotBusy || !email.trim()}
                      onClick={startForgotEmail}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="#4285f4" aria-hidden="true">
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                      </svg>
                      {t('wpaLogin.sendEmailOtp')}
                      {forgotMethods.maskedEmail ? (
                        <span style={{ fontSize: 12, fontWeight: 400, color: '#666' }}>({forgotMethods.maskedEmail})</span>
                      ) : null}
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  style={{ ...styles.modalBtn, marginTop: 14 }}
                  onClick={() => setShowForgotModal(false)}
                >
                  {t('wpa.layout.installGotIt')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WpaLoginPage;
